import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { http } from "../../lib/api";

const inputCls =
  "w-full border border-[#111111]/15 bg-white px-3.5 py-3 text-sm focus:border-[#0F4C81] focus:outline-none";

const GROUPS = {
  contact: ["phone", "whatsapp", "email", "address", "coverage", "hours", "company_registration"],
  social: ["instagram", "youtube", "linkedin"],
  hero: ["eyebrow", "headline_1", "headline_2", "subcopy", "primary_cta", "secondary_cta", "image"],
  seo: ["title", "description", "keywords"],
};

export const SettingsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const set = (group, key, value) =>
    setSettings((s) => ({ ...s, [group]: { ...s[group], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/settings", settings);
      toast.success("Website settings updated.");
    } catch {
      toast.error("Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-sm text-[#7A7F85]">Loading settings…</p>;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between gap-6">
        <p className="mako-label text-[#7A7F85]">Contact details, homepage hero, social and SEO</p>
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save"
          className="inline-flex items-center gap-2 bg-[#0F4C81] px-6 py-3 text-[0.625rem] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#1463A5] disabled:opacity-60"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "Saving" : "Save changes"}
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        {Object.entries(GROUPS).map(([group, keys]) => (
          <div key={group} className="border border-[#111111]/10 bg-white p-7">
            <p className="font-display text-base font-bold uppercase tracking-tight text-[#111111]">
              {group}
            </p>
            <div className="mt-6 space-y-5">
              {keys.map((key) => (
                <label key={key} className="block">
                  <span className="mako-label text-[#7A7F85]">{key.replace(/_/g, " ")}</span>
                  <div className="mt-2.5">
                    {key === "description" || key === "keywords" || key === "subcopy" ? (
                      <textarea
                        className={`${inputCls} min-h-[90px]`}
                        data-testid={`setting-${group}-${key}`}
                        value={settings[group]?.[key] || ""}
                        onChange={(e) => set(group, key, e.target.value)}
                      />
                    ) : (
                      <input
                        className={inputCls}
                        data-testid={`setting-${group}-${key}`}
                        value={settings[group]?.[key] || ""}
                        onChange={(e) => set(group, key, e.target.value)}
                      />
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
